import { useMemo } from "react"
import { groupBy } from "~/utils/tsUtils"

import { useRawForecasts } from "~/hooks/useForecasts"
import { api } from "~/utils/api"
import { getForecastsForWeeks } from "~/utils/forecastUtils"

export function useStorageLocationDataVariant(enabled: boolean) {
  const { data: variantLocationData, isLoading } =
    api.storageLocation.variantStorageLocationData.useQuery(undefined, { enabled })
  const { data: skuLocationData } = api.storageLocation.locationSkuData.useQuery(undefined, {
    enabled,
  })
  const { data: allForecasts } = useRawForecasts()
  return useMemo(() => {
    if (!variantLocationData?.length || !allForecasts || !skuLocationData) {
      return { data: undefined, isLoading }
    }

    return {
      data: new Map(
        Object.entries(groupBy(variantLocationData, "variantId")).map(
          ([variantId, locationData]) => {
            const skuLocationDataForVariant = skuLocationData.get(variantId)

            let thirteenWeeksForecast = 0
            let nextYearsForecast = 0
            for (const { forecasts } of allForecasts) {
              const f = forecasts[variantId]
              if (f) {
                thirteenWeeksForecast += getForecastsForWeeks(f, 13)
                nextYearsForecast += getForecastsForWeeks(f, 52)
              }
            }

            const out = locationData.map((locationData) => {
              const skuData = skuLocationDataForVariant?.get(locationData.locationId ?? "")

              let combinedWeight = 0
              let availableWeight = 0
              if (skuData) {
                for (const sku of skuData) {
                  combinedWeight += sku.weight
                  if (sku.quantity) {
                    availableWeight += sku.weight
                  }
                }
              }
              const availability = availableWeight / (combinedWeight || 1)

              return {
                thirteenWeeksForecast: thirteenWeeksForecast * combinedWeight,
                nextYearsForecast: nextYearsForecast * combinedWeight,
                weight: combinedWeight,
                locationId: locationData.locationId ?? "",
                stockLevel: locationData.quantity ?? 0,
                lastYearsSales: locationData.sales ?? 0,
                minimumStockLevel: locationData.minimumStockLevel,
                skuData,
                availability,
              }
            })

            return [variantId, out]
          },
        ),
      ),
      isLoading: !(variantLocationData || allForecasts || skuLocationData),
    }
  }, [variantLocationData, allForecasts, skuLocationData, isLoading])
}
